import { forwardRef } from 'react';

import { Indicator, useMantineTheme } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import Crown from '../assets/UI/crown.svg?react';
import { Avatar } from './UI/Avatar/Avatar';
import type { SelectProps } from './UI/Select/Select';
import { Select } from './UI/Select/Select';
import { UserWithAvatar } from './UI/UserWithAvatar/UserWithAvatar';

import type { User } from '../entities/User';
import { useWorkspaceUsersQuery } from '../hooks/useWorkspaceUsersQuery.hook';

type WorkSpaceUsersSelectProps = {
  user: User | null;
  placeholder?: string;
  withCrown?: boolean;
  label?: string;
  variant?: 'default' | 'unstyled';
  onChange: (userId: string) => void;
} & Omit<SelectProps, 'data'>;

export function WorkSpaceUsersSelect({
  user,
  placeholder,
  withCrown = false,
  label,
  variant = 'default',
  onChange,
  ...rest
}: WorkSpaceUsersSelectProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation('shared');

  const usersQuery = useWorkspaceUsersQuery();

  const nonDeletedUsers =
    usersQuery.data?.filter(usr => !usr.disabledAt || usr.id === user?.id) ||
    [];
  const userItems: SelectItemProps[] = nonDeletedUsers
    .map(user => ({
      value: user.id,
      firstname: user.firstName,
      lastname: user.lastName,
      avatarcolor: user.avatarColor,
      isDisabled: !!user.disabledAt,
      label: `${user.firstName} ${user.lastName}`,
    }))
    .sort((a, b) => a.firstname.localeCompare(b.firstname));

  const icon = user ? (
    <Avatar size="md" color={user.avatarColor} isDisabled={!!user.disabledAt}>
      {user.firstName[0].toUpperCase()}
      {user.lastName[0].toUpperCase()}
    </Avatar>
  ) : (
    <Avatar size="md" />
  );

  return (
    <Select
      variant={variant}
      searchable
      nothingFound={t('userSelect.noUserFound')}
      rightSection={<IconChevronDown size={14} color={theme.colors.gray[6]} />}
      placeholder={placeholder}
      size="xs"
      label={label}
      data={userItems}
      itemComponent={SelectItem}
      value={user?.id}
      onChange={onChange}
      withinPortal
      icon={withCrown ? <CrownIconWrapper>{icon}</CrownIconWrapper> : icon}
      {...rest}
    />
  );
}

interface SelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  firstname: string;
  lastname: string;
  label: string;
  avatarcolor: string;
  isDisabled: boolean;
  value: string;
}

const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  (
    { firstname, lastname, avatarcolor, isDisabled, ...props }: SelectItemProps,
    ref,
  ) => {
    return (
      <UserWithAvatar
        ref={ref}
        w="100%"
        avatarColor={avatarcolor}
        firstName={firstname}
        lastName={lastname}
        isDisabled={isDisabled}
        {...props}
      />
    );
  },
);

SelectItem.displayName = 'SelectItem';

function CrownIconWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Indicator
      label={<Crown />}
      offset={3}
      styles={{ indicator: { padding: 0, background: 'transparent' } }}
    >
      {children}
    </Indicator>
  );
}
